/**
 * The History popup's Prediction tab maths: the component wear model for
 * element readings (visualizer.js updatePredictionTab and its helpers) and
 * the threshold-crossing forecast for live RFIMS readings
 * (updateLivePredictionTab). Pure; the DOM writes become returned fields.
 */
import type { HistoryPoint, NormalRange } from '../types';
import { numericText } from './statusDerivation';

export interface SensorProfile {
  /** Per day. */
  degradationRate: number;
  type: string;
  failMode: 'increasing' | 'decreasing';
  ageYears: number;
  unit: string;
}

/** The 31-bit string hash the original used to vary each sensor's profile and seed its correlation series. */
export function hashIds(a: string, b: string): number {
  let hash = 0;
  const str = a + b;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

/**
 * Profile by the first matching keyword in the data id or subsystem id
 * (table order), then age ×(0.7 to 1.3) and rate ×(0.8 to 1.2) from the id hash.
 */
export function getSensorPredictionInfo(dataId: string | null | undefined, subsystemId: string | null | undefined): SensorProfile {
  const id = (dataId || '').toLowerCase();
  const subsystem = (subsystemId || '').toLowerCase();

  const sensorProfiles: Record<string, SensorProfile> = {
    // Cooling fan sensors - fastest degradation (bearings wear)
    fan: { degradationRate: 0.008, type: 'speed', failMode: 'decreasing', ageYears: 3.2, unit: 'RPM' },
    rpm: { degradationRate: 0.008, type: 'speed', failMode: 'decreasing', ageYears: 3.2, unit: 'RPM' },
    cooling: { degradationRate: 0.006, type: 'mixed', failMode: 'increasing', ageYears: 2.8, unit: '' },

    // Motor/antenna sensors - medium degradation
    motor: { degradationRate: 0.01, type: 'current', failMode: 'increasing', ageYears: 4.1, unit: 'A' },
    az: { degradationRate: 0.009, type: 'position', failMode: 'increasing', ageYears: 4.5, unit: 'A' },
    el: { degradationRate: 0.009, type: 'position', failMode: 'increasing', ageYears: 4.3, unit: 'A' },
    bearing: { degradationRate: 0.012, type: 'mechanical', failMode: 'increasing', ageYears: 3.8, unit: '' },

    // HPA/amplifier sensors - thermal cycling degradation
    hpa: { degradationRate: 0.004, type: 'power', failMode: 'decreasing', ageYears: 2.5, unit: 'W' },
    sspa: { degradationRate: 0.003, type: 'power', failMode: 'decreasing', ageYears: 2.2, unit: 'W' },
    power: { degradationRate: 0.003, type: 'power', failMode: 'decreasing', ageYears: 2.0, unit: 'W' },
    efficiency: { degradationRate: 0.002, type: 'efficiency', failMode: 'decreasing', ageYears: 2.5, unit: '%' },

    // LNA sensors - slow degradation
    lna: { degradationRate: 0.002, type: 'gain', failMode: 'decreasing', ageYears: 1.8, unit: 'dB' },
    gain: { degradationRate: 0.002, type: 'gain', failMode: 'decreasing', ageYears: 1.5, unit: 'dB' },
    noise: { degradationRate: 0.003, type: 'noise', failMode: 'increasing', ageYears: 2.0, unit: 'dB' },

    // Temperature sensors - varies by location
    temp: { degradationRate: 0.005, type: 'temperature', failMode: 'increasing', ageYears: 2.7, unit: '°C' },
    temperature: { degradationRate: 0.005, type: 'temperature', failMode: 'increasing', ageYears: 2.7, unit: '°C' },

    // Signal/communication sensors
    signal: { degradationRate: 0.002, type: 'signal', failMode: 'decreasing', ageYears: 1.2, unit: 'dBm' },
    snr: { degradationRate: 0.001, type: 'signal', failMode: 'decreasing', ageYears: 1.0, unit: 'dB' },
    ber: { degradationRate: 0.001, type: 'error', failMode: 'increasing', ageYears: 1.5, unit: '' },
    lock: { degradationRate: 0.001, type: 'status', failMode: 'decreasing', ageYears: 1.0, unit: '' },

    // Network/infrastructure
    throughput: { degradationRate: 0.001, type: 'bandwidth', failMode: 'decreasing', ageYears: 0.8, unit: 'Gbps' },

    // Waveguide sensors
    pressure: { degradationRate: 0.002, type: 'pressure', failMode: 'decreasing', ageYears: 1.5, unit: 'PSI' },
    vswr: { degradationRate: 0.002, type: 'ratio', failMode: 'increasing', ageYears: 1.8, unit: '' },
  };

  let profile: SensorProfile = { degradationRate: 0.003, type: 'generic', failMode: 'increasing', ageYears: 2.0, unit: '' };
  for (const key of Object.keys(sensorProfiles)) {
    if (id.indexOf(key) !== -1 || subsystem.indexOf(key) !== -1) {
      profile = { ...sensorProfiles[key] };
      break;
    }
  }

  const variation = (Math.abs(hashIds(dataId ?? '', subsystemId ?? '')) % 100) / 100;
  profile.ageYears = profile.ageYears * (0.7 + variation * 0.6);
  profile.degradationRate = profile.degradationRate * (0.8 + variation * 0.4);
  return profile;
}

export interface Regression {
  slope: number;
  intercept: number;
  /** Clamped at 0. */
  r2: number;
}

/** Least squares over the array index. */
export function calculateLinearRegression(values: number[]): Regression {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] || 0, r2: 0 };

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let sumYY = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += values[i];
    sumXY += i * values[i];
    sumXX += i * i;
    sumYY += values[i] * values[i];
  }
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const meanY = sumY / n;
  const ssTotal = sumYY - n * meanY * meanY;
  let ssResidual = 0;
  for (let j = 0; j < n; j++) {
    const predicted = intercept + slope * j;
    ssResidual += Math.pow(values[j] - predicted, 2);
  }
  const r2 = ssTotal > 0 ? 1 - ssResidual / ssTotal : 0;
  return { slope, intercept, r2: Math.max(0, r2) };
}

/**
 * Days to failure: readings to the threshold (normal high ×1.3 when the
 * failure mode is increasing, normal low ×0.7 when decreasing) ÷ 24; when
 * that gives nothing, a negative or more than 730 days, the degradation
 * model (remaining health ÷ rate, where the accumulated wear is the profile's
 * rate × age as a fraction capped at 1, see PORT_NOTES.md); ×0.7 when the
 * newest quarter of the history moved more than 5 % in the failing
 * direction; clamped 7 to 730. `values` are newest first.
 */
export function calculateIntelligentPrediction(
  currentValue: number,
  values: number[],
  regression: Regression,
  normalRange: NormalRange | null | undefined,
  sensorInfo: SensorProfile,
): number {
  let daysUntilFailure: number | undefined;

  if (normalRange && Math.abs(regression.slope) > 0.0001) {
    const threshold = sensorInfo.failMode === 'increasing' ? normalRange.high * 1.3 : normalRange.low * 0.7;
    const valueChange = threshold - currentValue;
    const readingsUntilThreshold = Math.abs(valueChange / regression.slope);
    daysUntilFailure = readingsUntilThreshold / 24;
  }

  if (!daysUntilFailure || daysUntilFailure > 730 || daysUntilFailure < 0) {
    const accumulatedWear = wearFraction(sensorInfo);
    const remainingHealth = Math.max(0.1, 1 - accumulatedWear);
    daysUntilFailure = remainingHealth / sensorInfo.degradationRate;
  }

  if (values.length >= 8) {
    const quarterLen = Math.floor(values.length / 4);
    const firstQuarter = values.slice(-quarterLen); // oldest
    const lastQuarter = values.slice(0, quarterLen); // newest
    const avgFirst = firstQuarter.reduce((a, b) => a + b, 0) / quarterLen;
    const avgLast = lastQuarter.reduce((a, b) => a + b, 0) / quarterLen;
    const recentTrend = avgLast - avgFirst;
    if (
      (sensorInfo.failMode === 'increasing' && recentTrend > 0) ||
      (sensorInfo.failMode === 'decreasing' && recentTrend < 0)
    ) {
      const acceleration = Math.abs(recentTrend) / Math.abs(avgFirst || 1);
      if (acceleration > 0.05) daysUntilFailure *= 0.7;
    }
  }

  return Math.min(730, Math.max(7, daysUntilFailure));
}

/** Base 75, ±sample size, ±fit, ±horizon, +5 for a known type, clamped 50 to 95. */
export function calculatePredictionConfidence(
  values: number[],
  regression: Regression,
  sensorInfo: SensorProfile,
  daysUntilFailure: number,
): number {
  let confidence = 75;
  if (values.length >= 50) confidence += 10;
  else if (values.length >= 20) confidence += 5;
  else if (values.length < 10) confidence -= 10;

  if (regression.r2 > 0.8) confidence += 8;
  else if (regression.r2 > 0.5) confidence += 3;
  else confidence -= 5;

  if (daysUntilFailure > 365) confidence -= 10;
  else if (daysUntilFailure > 180) confidence -= 5;
  else if (daysUntilFailure < 30) confidence += 5;

  if (sensorInfo.type !== 'generic') confidence += 5;
  return Math.max(50, Math.min(95, Math.round(confidence)));
}

/** The seven recommended-action tiers. */
export function generateRecommendedAction(daysUntilFailure: number, health: number): string {
  if (daysUntilFailure < 14) {
    return 'CRITICAL: Immediate replacement required. Equipment may fail within ' + Math.round(daysUntilFailure) + ' days.';
  }
  if (daysUntilFailure < 30) return 'URGENT: Schedule replacement within 2 weeks. Order spare parts immediately.';
  if (daysUntilFailure < 60) {
    return (
      'Schedule preventive maintenance within ' + Math.round(daysUntilFailure / 2) + ' days. Verify spare parts availability.'
    );
  }
  if (daysUntilFailure < 90) return 'Plan replacement during next scheduled maintenance window.';
  if (health < 70) return 'Monitor closely. Consider early replacement if degradation accelerates.';
  if (health < 85) return 'Continue routine monitoring. Schedule inspection in next maintenance cycle.';
  return 'System operating normally. Continue standard monitoring procedures.';
}

/** Numeric values of a history (a ratio's leading number; digits, '.', '-' kept), non-numeric points dropped, order preserved. */
export function parseHistoryValues(history: HistoryPoint[]): number[] {
  return history.map((h) => parseFloat(numericText(String(h.value)).replace(/[^0-9.-]/g, ''))).filter((v) => !isNaN(v));
}

/** Accumulated wear as a fraction: the profile's degradation rate taken per year × age in years, capped at 1. */
export function wearFraction(profile: Pick<SensorProfile, 'degradationRate' | 'ageYears'>): number {
  return Math.min(1, profile.degradationRate * profile.ageYears);
}

/** Health points the wear removes: the fraction scaled to at most 30 points. */
export const WEAR_HEALTH_POINTS = 30;

export function healthColour(health: number): string {
  return health > 70 ? '#4CAF50' : health > 40 ? '#FF9800' : '#F44336';
}

/** "Jan 5, 2027". */
export function formatPredictionDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Local-calendar day arithmetic, as the original's setDate(getDate() + n). */
export function addDaysMs(nowMs: number, days: number): number {
  const d = new Date(nowMs);
  d.setDate(d.getDate() + days);
  return d.getTime();
}

export type PredictionSeverity = 'critical' | 'warning' | '';

export interface InsufficientPrediction {
  kind: 'insufficient';
  message: string;
}

export interface ComponentPrediction {
  kind: 'component';
  /** 0 to 100 after the wear term, floor 10 when the profile degrades. */
  health: number;
  healthColour: string;
  daysUntilFailure: number;
  failureDateMs: number;
  failureDateText: string;
  severity: PredictionSeverity;
  confidence: number;
  /** Rising, Declining or Stable over time (the history is fitted oldest to newest). */
  trendDirection: 'Rising' | 'Declining' | 'Stable';
  /** |slope| to three decimals. */
  trendRate: string;
  trendText: string;
  recommendedAction: string;
  profile: SensorProfile;
  regression: Regression;
}

export interface ComponentPredictionInput {
  /** Newest first, as shipped. */
  history: HistoryPoint[];
  dataId: string;
  subsystemId: string;
  normalRange?: NormalRange | null;
  nowMs: number;
}

/**
 * Component mode: health 100 − deviation from the normal midpoint ×30, minus
 * the profile's wear fraction scaled to at most 30 points, floor 10; the
 * trend is fitted oldest to newest; days to failure from
 * calculateIntelligentPrediction; severity critical below 30 days, warning
 * below 90; confidence from calculatePredictionConfidence. (The wear term
 * and the fit order are defect fixes, see PORT_NOTES.md.)
 */
export function computeComponentPrediction(input: ComponentPredictionInput): ComponentPrediction | InsufficientPrediction {
  if (!input.history || input.history.length < 2) {
    return { kind: 'insufficient', message: 'Insufficient historical data for prediction' };
  }
  const values = parseHistoryValues(input.history);
  if (values.length < 2) return { kind: 'insufficient', message: 'Insufficient historical data for prediction' };

  const sensorInfo = getSensorPredictionInfo(input.dataId, input.subsystemId);
  // The history is newest first; the fit runs over time, oldest to newest.
  const regression = calculateLinearRegression(values.slice().reverse());
  const trendDirection = regression.slope > 0.001 ? 'Rising' : regression.slope < -0.001 ? 'Declining' : 'Stable';
  const trendRate = Math.abs(regression.slope).toFixed(3);

  let health = 85;
  const currentValue = values[0];
  if (input.normalRange) {
    const normalMid = (input.normalRange.high + input.normalRange.low) / 2;
    const normalSpan = input.normalRange.high - input.normalRange.low;
    const deviation = Math.abs(currentValue - normalMid) / (normalSpan / 2);
    health = Math.max(0, Math.min(100, Math.round(100 - deviation * 30)));
  }
  if (sensorInfo.degradationRate > 0) {
    health = Math.max(10, Math.min(100, Math.round(health - wearFraction(sensorInfo) * WEAR_HEALTH_POINTS)));
  }

  const daysUntilFailure = calculateIntelligentPrediction(currentValue, values, regression, input.normalRange, sensorInfo);
  const failureDateMs = addDaysMs(input.nowMs, Math.round(daysUntilFailure));
  const confidence = calculatePredictionConfidence(values, regression, sensorInfo, daysUntilFailure);

  return {
    kind: 'component',
    health,
    healthColour: healthColour(health),
    daysUntilFailure,
    failureDateMs,
    failureDateText: formatPredictionDate(failureDateMs),
    severity: daysUntilFailure < 30 ? 'critical' : daysUntilFailure < 90 ? 'warning' : '',
    confidence,
    trendDirection,
    trendRate,
    trendText: trendDirection + ' (' + trendRate + ' ' + (sensorInfo.unit || '') + '/reading)',
    recommendedAction: generateRecommendedAction(daysUntilFailure, health),
    profile: sensorInfo,
    regression,
  };
}

// ------------------------------------------------------------------- live

export interface LiveMetricConfig {
  unit: string;
  /** Both limits matter (temperature, humidity). */
  bidir: boolean;
  /** Live metric names that override the fallback limits. */
  liveHi?: string;
  liveLo?: string;
  hiScale?: number;
  hi: number;
  lo: number;
  what: string;
}

/** Per-metric limits for the live RFIMS readings, keyed `${subsystemId}|${dataId}`. */
export const LIVE_METRIC_CONFIG: Record<string, LiveMetricConfig> = {
  'environmental|temperature': { unit: '°C', bidir: true, liveHi: 'temp_max_c', liveLo: 'temp_min_c', hi: 35, lo: 10, what: 'temperature limit' },
  'environmental|humidity': { unit: '%', bidir: true, liveHi: 'humidity_max_pct', liveLo: 'humidity_min_pct', hi: 80, lo: 20, what: 'humidity limit' },
  'host_health|cpu_load': { unit: '', bidir: false, hi: 4, lo: 0, what: 'load limit' },
  'host_health|disk_usage': { unit: 'GB', bidir: false, liveHi: 'disk_total_mib', hiScale: 1 / 1024, hi: 6, lo: 0, what: 'full' },
  'network|latency': { unit: 'ms', bidir: false, hi: 250, lo: 0, what: 'latency limit' },
  'network|packet_loss': { unit: '%', bidir: false, hi: 5, lo: 0, what: 'loss limit' },
};

export const LIVE_METRIC_DEFAULT: LiveMetricConfig = { unit: '', bidir: false, hi: 100, lo: 0, what: 'limit' };

export interface LivePredictionInput {
  /** Newest first. */
  history: HistoryPoint[];
  subsystemId: string;
  dataId: string;
  /** Latest value of a live metric by name (temp_max_c, ...), or null when unknown. */
  latest?: (metric: string) => number | null | undefined;
  nowMs: number;
}

export interface LivePrediction {
  kind: 'live';
  trendText: string;
  forecastText: string;
  severity: PredictionSeverity;
  confidence: number;
  /** The "Margin" circle. */
  margin: number;
  marginColour: string;
  recommendedAction: string;
  daysUntilCrossing: number | null;
  target: number | null;
  current: number;
  perDay: number;
  upper: number;
  lower: number;
}

/**
 * Live mode: per-day regression of the real series, projection to the limit
 * the trend heads for ("Reaches X ~ date (Nd)", "At/over limit now (X)",
 * "Stable - within limits"), confidence R² ×100 clamped 5 to 95 and capped
 * at 60 without a projected crossing, margin as in-band centredness or
 * headroom to the limit.
 */
export function computeLivePrediction(input: LivePredictionInput): LivePrediction | InsufficientPrediction {
  const cfg = LIVE_METRIC_CONFIG[input.subsystemId + '|' + input.dataId] || LIVE_METRIC_DEFAULT;
  const unitStr = cfg.unit === '°C' || cfg.unit === '%' ? cfg.unit : cfg.unit ? ' ' + cfg.unit : '';

  const vals: number[] = [];
  const times: number[] = [];
  for (const p of input.history.slice().reverse()) {
    const v = parseFloat(String(p.value).replace(/[^0-9.-]/g, ''));
    const t = new Date(p.datetime).getTime();
    if (!isNaN(v) && !isNaN(t)) {
      vals.push(v);
      times.push(t);
    }
  }
  if (vals.length < 2) return { kind: 'insufficient', message: 'Not enough data' };

  const reg = calculateLinearRegression(vals);
  const spanDays = (times[times.length - 1] - times[0]) / 86400000;
  const perDay = spanDays > 0 ? reg.slope * ((vals.length - 1) / spanDays) : 0;
  const current = vals[vals.length - 1];

  const liveOr = (name: string, scale: number | undefined, fallback: number): number => {
    const v = input.latest ? input.latest(name) : null;
    return typeof v === 'number' ? v * (scale || 1) : fallback;
  };
  const upper = cfg.liveHi ? liveOr(cfg.liveHi, cfg.hiScale, cfg.hi) : cfg.hi;
  const lower = cfg.bidir ? (cfg.liveLo ? liveOr(cfg.liveLo, 1, cfg.lo) : cfg.lo) : cfg.lo;

  const EPS = Math.max(Math.abs(upper - lower) * 0.0005, 1e-6);
  let days: number | null = null;
  let target: number | null = null;
  if (perDay > EPS && current < upper) {
    target = upper;
    days = (upper - current) / perDay;
  } else if (cfg.bidir && perDay < -EPS && current > lower) {
    target = lower;
    days = (lower - current) / perDay;
  }
  if (days !== null && (days < 0 || days > 3650)) {
    days = null;
    target = null;
  }

  const fmt = (n: number): string => (Math.abs(n) >= 100 ? Math.round(n) : n.toFixed(1)) + unitStr;

  const dirWord = perDay > EPS ? 'Rising' : perDay < -EPS ? 'Falling' : 'Stable';
  const rate = Math.abs(perDay);
  const rateStr = rate >= 0.01 ? rate.toFixed(2) : rate.toExponential(1);
  const trendText = dirWord === 'Stable' ? 'Stable' : dirWord + ' ' + rateStr + unitStr + '/day';

  let forecastText: string;
  let severity: PredictionSeverity = '';
  if (current >= upper) {
    forecastText = 'At/over limit now (' + fmt(current) + ')';
    severity = 'critical';
  } else if (cfg.bidir && current <= lower) {
    forecastText = 'At/under limit now (' + fmt(current) + ')';
    severity = 'critical';
  } else if (days !== null) {
    const dateStr = formatPredictionDate(addDaysMs(input.nowMs, Math.round(days)));
    forecastText = 'Reaches ' + fmt(target as number) + ' ~ ' + dateStr + ' (' + Math.round(days) + 'd)';
    severity = days < 30 ? 'critical' : days < 90 ? 'warning' : '';
  } else {
    forecastText = 'Stable - within limits';
  }

  let conf = Math.round((reg.r2 || 0) * 100);
  if (days === null) conf = Math.min(conf, 60);
  const confidence = Math.max(5, Math.min(95, conf));

  let margin: number;
  if (cfg.bidir) {
    const mid = (upper + lower) / 2;
    const half = (upper - lower) / 2 || 1;
    margin = Math.round(Math.max(0, 1 - Math.abs(current - mid) / half) * 100);
  } else {
    margin = Math.round(Math.max(0, Math.min(1, (upper - current) / (upper - lower || 1))) * 100);
  }

  let recommendedAction: string;
  if (current >= upper || (cfg.bidir && current <= lower)) {
    recommendedAction = 'Reading is at its alarm limit now. Check the live status and the site.';
  } else if (days !== null && days < 30) {
    recommendedAction = 'Trending toward ' + cfg.what + '; projected within ' + Math.round(days) + ' days. Investigate the cause now.';
  } else if (days !== null && days < 180) {
    recommendedAction = 'Slow trend toward ' + cfg.what + ' (~' + Math.round(days) + ' days). Keep monitoring.';
  } else {
    recommendedAction = 'Stable. No limit breach projected from the current trend.';
  }

  return {
    kind: 'live',
    trendText,
    forecastText,
    severity,
    confidence,
    margin,
    marginColour: healthColour(margin),
    recommendedAction,
    daysUntilCrossing: days,
    target,
    current,
    perDay,
    upper,
    lower,
  };
}
